import styled from '@emotion/styled'
import { Button as BaseButton } from '@leroy-merlin-br/backyard-react-ui'
import { css } from '@emotion/react'

const buttonCloseStyles = ({ theme, isActive }) => css`
  label: modal__button-close;

  position: absolute;

  top: ${theme.spacings.mega};
  left: ${theme.spacings.mega};

  color: ${isActive ? theme.colors.white : theme.colors.n400};

  ${theme.media.mega} {
    display: none;
  }
`

const buttonCloseTextStyles = ({ theme }) => css`
  label: modal__button-text;

  position: absolute;

  top: -28px;
  right: 0;

  display: none;
  align-items: center;

  font-size: ${theme.typography.subHeadings.kilo.fontSize};
  color: ${theme.colors.white};
  cursor: pointer;

  svg {
    display: block;

    width: 10px;
    height: 10px;

    margin-left: ${theme.spacings.byte};
  }

  ${theme.media.mega} {
    display: flex;
  }
`

const asideStyles = ({ theme }) => css`
  label: modal__aside;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  overflow: hidden;
  width: 100%;
  min-height: 100%;

  background: ${theme.colors.white};

  ${theme.media.mega} {
    width: 180px;
  }
`

const headerStyles = ({ theme }) => css`
  label: modal__header;

  padding-top: ${theme.spacings.mega};
  padding-bottom: ${theme.spacings.peta};
  border-bottom: 1px solid ${theme.colors.n200};

  ${theme.media.mega} {
    padding-top: ${theme.spacings.giga};
    padding-bottom: ${theme.spacings.giga};

    border: transparent;
  }
`

export const iconExtraStyles = ({ theme }) => css`
  label: modal__icon-extra;

  position: absolute;

  top: 0;
  right: 0;

  svg {
    display: block;

    width: 14px;
    height: 14px;
    margin-top: 10px;
    margin-right: ${theme.spacings.kilo};

    color: ${theme.colors.n300};
    cursor: pointer;
  }
`

const pictureStyles = ({ theme }) => css`
  label: modal__picture;

  position: relative;

  width: 95px;
  height: 95px;
  margin: 0 auto;
  margin-bottom: ${theme.spacings.mega};

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid ${theme.colors.n100};
  border-radius: 50px;

  cursor: pointer;
`

const pictureImageStyles = ({ theme }) => css`
  label: modal__picture-image;

  img,
  svg {
    display: block;

    width: 100%;
    height: 100%;
  }

  img {
    border-radius: 50px;
  }

  svg {
    padding: 10px;

    color: ${theme.colors.v700};
    fill: ${theme.colors.v700};
  }
`

const pictureIconStyles = ({ theme }) => css`
  label: modal__picture-icon;

  position: absolute;

  left: 0;
  bottom: 0;
  width: 28px;
  height: 28px;
  padding: 10px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${theme.colors.n300};
  border-radius: 50px;

  background: ${theme.colors.white};
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.25);
  transition: background ease-in 300ms;
`

const titleStyles = ({ theme }) => css`
  label: modal__title;

  display: block;

  padding: 0 ${theme.spacings.tera};

  color: ${theme.colors.n800};
  font-size: ${theme.typography.text.giga.fontSize};
  font-weight: ${theme.font.weight.semibold};
  line-height: ${theme.typography.subHeadings.mega.lineHeight};
  text-align: center;

  ${theme.media.mega} {
    font-size: ${theme.typography.subHeadings.mega.fontSize};
  }
`

const subTitleStyles = ({ theme }) => css`
  label: modal__subtitle;

  display: none;

  margin-bottom: ${theme.spacings.bit};

  color: ${theme.colors.n600};
  font-size: ${theme.typography.subHeadings.kilo.fontSize};
  line-height: 16px;
  text-align: center;

  ${theme.media.mega} {
    display: block;
  }
`

const textShareStyles = ({ theme }) => css`
  label: modal__text-share;

  position: absolute;

  top: 10px;
  right: ${theme.spacings.kilo};

  svg {
    display: block;

    width: 14px;
    height: 14px;

    color: ${theme.colors.white};
  }
`

const contentScrollStyles = ({ theme }) => css`
  label: modal__content-scroll;

  width: 100%;
  max-height: 60vh;
  min-height: 100%;
  overflow-y: auto;

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.n300};
    border-radius: 20px;
    border: 8px solid ${theme.colors.white};
  }

  ${theme.media.mega} {
    width: calc(100% - 180px);
    max-height: 450px;

    background: ${theme.colors.n100};

    &::-webkit-scrollbar {
      width: 20px;
    }

    &::-webkit-scrollbar-thumb {
      border: 8px solid ${theme.colors.n100};
    }
  }
`

const contentStyles = ({ theme }) => css`
  label: modal__content;

  padding: ${theme.spacings.tera};
`

const footerStyles = ({ theme }) => css`
  label: modal__footer;

  position: relative;

  padding: ${theme.spacings.mega} ${theme.spacings.giga};
  background: ${theme.colors.white};

  box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.2);

  ${theme.media.mega} {
    box-shadow: none;
    background: transparent;
  }
`

const baseTextStyles = ({ theme }) => css`
  label: modal__base-text;

  color: ${theme.colors.n600};
  font-size: ${theme.typography.subHeadings.kilo.fontSize};
  line-height: ${theme.typography.subHeadings.kilo.lineHeight};
  text-align: center;
`

const textExpireStyles = ({ theme }) => css`
  label: modal__text-expire;

  display: block;

  margin-bottom: ${theme.spacings.mega};
`

const textRemainingStyles = ({ theme }) => css`
  label: modal__text-remaining;

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  margin-top: ${theme.spacings.mega};

  svg {
    display: block;
    margin-top: 2px;
    margin-right: ${theme.spacings.bit};
  }
`

const buttonLabelStyles = ({ theme }) => css`
  label: modal__button-label;

  display: none;

  margin-bottom: ${theme.spacings.byte};

  font-size: ${theme.typography.subHeadings.kilo.fontSize};
  line-height: 11px;
  text-align: center;
  color: ${theme.colors.p500};

  ${theme.media.mega} {
    display: block;
  }
`

const buttonActiveStyles = ({ theme, isActive }) =>
  isActive &&
  css`
    label: modal__button--active;

    color: ${theme.colors.v700};
    background: ${theme.colors.white};
    pointer-events: none;

    svg {
      margin-right: ${theme.spacings.byte};
      height: 15px;
      width: 15px;
    }

    ${theme.media.mega} {
      color: ${theme.colors.white};
      background: ${theme.colors.v700};
    }
  `

const styleWrapper = ({ theme }) => css`
  label: modal__wrapper;

  position: relative;

  width: 100%;

  display: flex;
  flex-direction: column;
  overflow: hidden;

  border-radius: 10px;
  background: ${theme.colors.white};

  ${theme.media.mega} {
    flex-direction: row-reverse;
    min-height: 450px;
    overflow: unset;
    border-radius: 0;
  }
`

export const ButtonClose = styled('div')(buttonCloseStyles)
export const ButtonCloseText = styled('span')(buttonCloseTextStyles)
export const Aside = styled('div')(asideStyles)
export const Header = styled('div')(headerStyles)
export const IconExtra = styled('div')(iconExtraStyles)
export const Picture = styled('div')(pictureStyles)
export const PictureImage = styled('div')(pictureImageStyles)
export const PictureIcon = styled('div')(pictureIconStyles)
export const Title = styled('span')(titleStyles)
export const SubTitle = styled('span')(subTitleStyles)
export const TextShare = styled('span')(textShareStyles)
export const Content = styled('div')(contentStyles)
export const ContentScroll = styled('div')(contentScrollStyles)
export const Footer = styled('div')(footerStyles)
export const TextExpire = styled('span')(baseTextStyles, textExpireStyles)
export const TextRemaining = styled('span')(baseTextStyles, textRemainingStyles)
export const ButtonLabel = styled('span')(buttonLabelStyles)
export const ButtonSend = styled(BaseButton)(buttonActiveStyles)

const styleWrapperActive = ({ theme, isActive }) =>
  isActive &&
  css`
    label: modal__wrapper--active;

    ${Aside} {
      background-image: linear-gradient(
        150deg,
        ${theme.colors.f700} 5%,
        ${theme.colors.v500} 100%
      );
    }

    ${IconExtra} svg {
      color: ${theme.colors.white};
    }

    ${Picture} {
      border-color: ${theme.colors.f500};
    }

    ${PictureImage} svg {
      color: ${theme.colors.white};
      fill: ${theme.colors.white};
    }

    ${PictureIcon} {
      color: ${theme.colors.f300};
      background: ${theme.colors.f500};
    }

    ${Title} {
      color: ${theme.colors.white};
    }

    ${SubTitle} {
      color: ${theme.colors.f300};
    }

    ${TextShare} {
      color: ${theme.colors.f300};
    }

    ${theme.media.mega} {
      ${TextRemaining} {
        color: ${theme.colors.f300};
      }

      ${TextExpire} {
        color: ${theme.colors.f300};
      }
    }
  `

export const Wrapper = styled('div')(styleWrapper, styleWrapperActive)
